// V2 activity client (BRIEFS C2.4, SPEC §6).
//
//   track(type, fields)       queue one event (types: services/v2_events.py EVENT_TYPES)
//   useActivityArea('notes')  the page tells which area its active time belongs to
//   markActive()              e.g. a playing video keeps the clock running without input
//
// The queue is kept in localStorage too, so events survive a crash or a closed window and
// are sent on the next start (the server ignores repeats by client_event_id).
// Sent every 5 s, and when the page is hidden / closed (fetch keepalive; sendBeacon cannot
// carry the Authorization header, so it is only a last resort without a token).
// Idle: no mouse/keyboard/scroll/touch for `idle_seconds` → idle_start; next input →
// idle_end. Active time is counted only while not idle and the page is visible, and sent as
// `heartbeat` {duration_ms, details.area} every 30 s of ACTIVE time (and when the area
// changes, on idle and on close, so nothing is lost).
import { useEffect } from 'react';
import { postActivity, tokens, V2_BASE } from '../../api/v2';

const QUEUE_KEY = 'v2_activity_queue';
const FLUSH_MS = 5000;
const TICK_MS = 1000;
const HEARTBEAT_MS = 30000;
const MAX_TICK_GAP_MS = 5000;    // a longer gap between ticks (sleep, throttling) is not counted
const MAX_BATCH = 200;           // server limit per call
const KEEPALIVE_BATCH = 50;      // keepalive bodies must stay under 64 KB
const MAX_QUEUE = 5000;          // safety cap if the server is unreachable for a long time
export const AREAS = ['notes', 'test', 'video', 'revision', 'home'];

const state = {
  started: false,
  queue: [],
  sending: false,
  area: 'home',
  idle: false,
  idleSeconds: 120,
  lastInputAt: Date.now(),
  idleSince: null,
  lastTick: Date.now(),
  activeMs: 0,
  timers: [],
};

// ------------------------------------------------------------------ queue

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function save() {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(state.queue)); } catch { /* full/private */ }
}

function load() {
  try {
    const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(q) ? q : [];
  } catch { return []; }
}

function removeSent(ids) {
  const sent = new Set(ids);
  state.queue = state.queue.filter((e) => !sent.has(e.client_event_id));
  save();
}

export function track(type, fields = {}) {
  const ev = { client_event_id: uuid(), type, at: new Date().toISOString(), ...fields };
  state.queue.push(ev);
  if (state.queue.length > MAX_QUEUE) state.queue.splice(0, state.queue.length - MAX_QUEUE);
  save();
  return ev;
}

export async function flush() {
  if (state.sending || state.queue.length === 0) return;
  if (!tokens.device && !tokens.access) return; // not signed in: keep them for later
  state.sending = true;
  const batch = state.queue.slice(0, MAX_BATCH);
  try {
    await postActivity(batch);
    removeSent(batch.map((e) => e.client_event_id));
  } catch (err) {
    // 422 = the batch itself is bad and would be refused for ever → drop it.
    // Anything else (offline, 5xx, signed out) → keep it and try again later.
    if (err && err.response && err.response.status === 422) {
      // eslint-disable-next-line no-console
      console.warn('activity batch refused by the server', err.response.data);
      removeSent(batch.map((e) => e.client_event_id));
    }
  } finally {
    state.sending = false;
  }
}

// When the page is hidden or closing: send with keepalive so the request outlives the page.
function flushKeepalive() {
  if (state.queue.length === 0) return;
  const url = `${V2_BASE}/activity`;
  const token = tokens.access;
  for (let i = 0; i < state.queue.length; i += KEEPALIVE_BATCH) {
    const batch = state.queue.slice(i, i + KEEPALIVE_BATCH);
    const body = JSON.stringify({ events: batch });
    if (token && typeof fetch === 'function') {
      try {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
        }).then((res) => {
          if (res.ok || res.status === 422) removeSent(batch.map((e) => e.client_event_id));
        }).catch(() => { /* stays queued (also in localStorage) */ });
        continue;
      } catch { /* fall through */ }
    }
    // No token in memory: a beacon cannot authenticate, so the events simply stay in
    // localStorage and go out with the next start.
  }
}

// ------------------------------------------------------------------ active time

function heartbeat() {
  const ms = Math.round(state.activeMs);
  state.activeMs = 0;
  if (ms >= 1000) track('heartbeat', { duration_ms: ms, details: { area: state.area } });
}

function goIdle(now) {
  if (state.idle) return;
  heartbeat();
  state.idle = true;
  state.idleSince = state.lastInputAt + state.idleSeconds * 1000;
  track('idle_start', { at: new Date(Math.min(state.idleSince, now)).toISOString(),
    details: { area: state.area, idle_seconds: state.idleSeconds } });
}

function onInput() {
  const now = Date.now();
  state.lastInputAt = now;
  if (state.idle) {
    state.idle = false;
    const idleMs = state.idleSince ? Math.max(0, now - state.idleSince) : 0;
    state.idleSince = null;
    state.lastTick = now;
    track('idle_end', { duration_ms: idleMs, details: { area: state.area } });
  }
}

export function markActive() { onInput(); }

function tick() {
  const now = Date.now();
  const gap = now - state.lastTick;
  state.lastTick = now;
  const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  if (!state.idle && now - state.lastInputAt >= state.idleSeconds * 1000) goIdle(now);
  if (!state.idle && visible && gap > 0 && gap <= MAX_TICK_GAP_MS) state.activeMs += gap;
  if (state.activeMs >= HEARTBEAT_MS) heartbeat();
}

export function setArea(area) {
  const next = AREAS.includes(area) ? area : 'home';
  if (next === state.area) return;
  heartbeat(); // time so far belongs to the old area
  state.area = next;
}

export function getArea() { return state.area; }

export function setIdleSeconds(n) {
  const v = Number(n);
  if (Number.isFinite(v) && v >= 10) state.idleSeconds = v;
}

// A page says where its time goes; leaving the page returns to the previous area.
export function useActivityArea(area) {
  useEffect(() => {
    const prev = state.area;
    setArea(area);
    return () => setArea(prev === area ? 'home' : prev);
  }, [area]);
}

// ------------------------------------------------------------------ start / stop

const INPUT_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];

function onVisibility() {
  if (document.visibilityState === 'hidden') {
    heartbeat();
    flushKeepalive();
  } else {
    state.lastTick = Date.now();
    onInput();
  }
}

function onPageHide() {
  heartbeat();
  track('app_close', { details: { area: state.area } });
  flushKeepalive();
}

export function startActivity({ idleSeconds } = {}) {
  if (idleSeconds) setIdleSeconds(idleSeconds);
  if (state.started) return;
  state.started = true;
  const known = new Set(state.queue.map((e) => e.client_event_id));
  state.queue = load().filter((e) => e && !known.has(e.client_event_id)).concat(state.queue);
  state.lastInputAt = Date.now();
  state.lastTick = Date.now();
  track('app_open', { details: { path: window.location.pathname,
    desktop: Boolean(window.v2desktop) } });
  INPUT_EVENTS.forEach((t) => window.addEventListener(t, onInput, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  state.timers.push(setInterval(tick, TICK_MS));
  state.timers.push(setInterval(flush, FLUSH_MS));
  flush();
}

export function stopActivity() {
  if (!state.started) return;
  state.started = false;
  INPUT_EVENTS.forEach((t) => window.removeEventListener(t, onInput, { capture: true }));
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', onPageHide);
  state.timers.forEach(clearInterval);
  state.timers = [];
}

// For tests / debugging in the browser console.
export const _activityState = state;
